import type { TermCategory } from '../types';

export * from './withBlankLinks';

/**
 * 訪問時モーダル（表示層①）の抑制。
 *
 * キャンセル（閉じる・拒否）したら時刻を localStorage に保存し、一定時間は層①を抑制する。
 * 抑制時間の経過後は再び表示する。購入導線での再喚起（表示層②）は抑制対象外で、
 * 抑制中でも必ず表示する。同意したら抑制を解除する。
 *
 * 抑制は**ユーザーごと・規約タイプごと**に持つ。
 * - ユーザーごと: localStorage はブラウザー単位で共有されるため、キーにユーザーIDを含めないと
 *   同一ブラウザーでアカウントを切り替えた際に、前のアカウントの抑制が引き継がれてしまう。
 * - 規約タイプごと: 1つの規約をキャンセルしても、他に同意が必要な規約のモーダルは
 *   引き続き表示する必要があるため。
 */

/** localStorage キーの接頭辞。実際のキーは `${接頭辞}_${ユーザーID}_${規約タイプ}` */
export const RECONSENT_DISMISSED_AT_KEY_PREFIX = 'reconsentDismissedAt';

/** 本番環境の抑制時間（ミリ秒）。24時間 */
const RECONSENT_SUPPRESS_DURATION_PRODUCTION_MS = 24 * 60 * 60 * 1000;

/** 本番環境以外の抑制時間（ミリ秒）。5分。再表示の確認に丸1日待たずに済むよう短くしている */
const RECONSENT_SUPPRESS_DURATION_NON_PRODUCTION_MS = 5 * 60 * 1000;

/** 抑制時間（ミリ秒）。本番環境は24時間、それ以外の環境は5分 */
export const RECONSENT_SUPPRESS_DURATION_MS =
  process.env.NEXT_PUBLIC_ENV === 'production'
    ? RECONSENT_SUPPRESS_DURATION_PRODUCTION_MS
    : RECONSENT_SUPPRESS_DURATION_NON_PRODUCTION_MS;

/** ユーザーごと・規約タイプごとの抑制キーを組み立てる */
export const buildReconsentDismissedAtKey = (userId: number, category: TermCategory): string =>
  `${RECONSENT_DISMISSED_AT_KEY_PREFIX}_${userId}_${category}`;

/**
 * 当該ユーザー・当該規約タイプのキャンセル時刻を保存する。
 * localStorage が使えない環境（プライベートモード等）では何もしない。
 * ユーザーIDが未確定の場合は、誰の抑制か特定できないため保存しない。
 */
export const saveReconsentDismissedAt = (
  userId: number | undefined,
  category: TermCategory,
  now: number = Date.now(),
): void => {
  if (userId === undefined) return;

  try {
    localStorage.setItem(buildReconsentDismissedAtKey(userId, category), String(now));
  } catch {
    // 保存できない場合は抑制が効かず毎回表示されるだけなので、致命的ではない
  }
};

/** 同意後に当該ユーザー・当該規約タイプの抑制を解除する */
export const clearReconsentDismissedAt = (userId: number | undefined, category: TermCategory): void => {
  if (userId === undefined) return;

  try {
    localStorage.removeItem(buildReconsentDismissedAtKey(userId, category));
  } catch {
    // 何もしない
  }
};

/**
 * 当該ユーザー・当該規約タイプの訪問時モーダルが抑制中か。
 * 保存値がない・不正・抑制時間が経過済みなら false（＝表示してよい）。
 * ユーザーIDが未確定の場合も false を返す。判定できないときは、
 * モーダルを出さないより出すほうが安全側であるため。
 */
export const isReconsentSuppressed = (
  userId: number | undefined,
  category: TermCategory,
  now: number = Date.now(),
): boolean => {
  if (userId === undefined) return false;

  try {
    const stored = localStorage.getItem(buildReconsentDismissedAtKey(userId, category));
    if (!stored) return false;

    const dismissedAt = Number(stored);
    if (!Number.isFinite(dismissedAt)) return false;

    const elapsed = now - dismissedAt;
    // 端末の時刻変更で未来の値が入った場合も抑制扱いにはしない
    if (elapsed < 0) return false;

    return elapsed < RECONSENT_SUPPRESS_DURATION_MS;
  } catch {
    return false;
  }
};
